/**
 * Configuration Defaults
 * Environment-based default values
 */

import type { AppPublicConfig, AppServerConfig, Environment } from "./types";

/**
 * Get API base URL from build-time injection or environment fallback
 */
function getApiBaseUrl(env: Environment): string {
  // Check for build-time injected __API_BASE_URL__ (from webpack DefinePlugin)
  // @ts-ignore - __API_BASE_URL__ is injected by webpack DefinePlugin
  if (typeof __API_BASE_URL__ !== "undefined") {
    // @ts-ignore
    return __API_BASE_URL__;
  }
  
  // Fallback to environment-based defaults
  switch (env) {
    case "dev":
      return "https://proxy-dev.exceloption.com";
    case "stage":
      return "https://proxy-stage.exceloption.com";
    case "prod":
      return "https://proxy.exceloption.com";
  }
}

/**
 * Clamp cache bucket size to safe bounds
 */
function clampCacheBucketMs(value: number | undefined): number {
  const DEFAULT = 5000;
  const MIN = 1000;
  const MAX = 60000;

  if (value === undefined || value === null || isNaN(value)) {
    return DEFAULT;
  }

  if (value < MIN) {
    return MIN;
  }

  if (value > MAX) {
    return MAX;
  }

  return value;
}

/**
 * Get default public configuration for an environment
 */
export function getDefaultAppPublicConfig(env: Environment): AppPublicConfig {
  const apiBaseUrl = getApiBaseUrl(env);

  return {
    env,
    public: {
      apiBaseUrl,
      featureFlags: {
        diagnostics: env === "dev",
      },
      refreshLimits: {
        maxAutoRefreshPerMinute: 6,
        maxManualRefreshPerMinute: 30,
        minSecondsBetweenRefreshSameCell: 10,
      },
      cacheBucketMs: 5000,
    },
  };
}

/**
 * Get default server configuration for an environment
 */
export function getDefaultAppServerConfig(env: Environment): AppServerConfig {
  const publicConfig = getDefaultAppPublicConfig(env);

  return {
    env: publicConfig.env,
    public: publicConfig.public,
    private: {
      massiveApiKey: undefined,
    },
  };
}
